import { Rng } from '../../core/rng';
import {
  CAR_BODIES, PLATE_REGIONS, PRAGYA_BODIES, SLOGAN_COLORS, TAXI_BODIES, TAXI_CORNERS, TROTRO_BODIES,
  TROTRO_SLOGANS, TROTRO_STRIPE_SETS, TRUCK_BODIES, WINDSCREEN_BANNERS,
} from '../../content/vehicleLooks';
import type { Livery, VehicleSpec } from './types';

function plate(rng: Rng): string {
  return `${rng.pick(PLATE_REGIONS)} ${rng.int(1000, 9999)}-${rng.pick(['19', '20', '21', '22', '23', '24', '25'])}`;
}

function stripes(rng: Rng, spec: VehicleSpec): Livery['stripes'] {
  const colors = rng.pick(TROTRO_STRIPE_SETS);
  const windowBottom = Math.min(...spec.windows.map((w) => w.v0));
  const band = 0.1;
  return colors.map((color, i) => ({ color, v0: windowBottom - 0.12 - band * (i + 1), v1: windowBottom - 0.12 - band * i }));
}

/** Garage paint stripes, laid in bands just under the windows. */
export function paintStripes(spec: VehicleSpec, colors: readonly string[]): Livery['stripes'] {
  const windowBottom = Math.min(...spec.windows.map((w) => w.v0));
  const band = 0.1;
  return colors.map((color, i) => ({ color, v0: windowBottom - 0.12 - band * (i + 1), v1: windowBottom - 0.12 - band * i }));
}

export function trotroLivery(seed: number, spec: VehicleSpec): Livery {
  const rng = new Rng(seed);
  const slogan = rng.pick(TROTRO_SLOGANS);
  return {
    body: rng.pick(TROTRO_BODIES),
    stripes: stripes(rng, spec),
    sideText: slogan,
    sideTextColor: rng.pick(SLOGAN_COLORS),
    rearText: rng.next() > 0.3 ? rng.pick(TROTRO_SLOGANS) : slogan,
    frontBand: rng.pick(WINDSCREEN_BANNERS),
    plate: plate(rng),
  };
}

/** The Fish as in the reference photo: white, black rub strip, orange-yellow stripe low down. */
export function fishLivery(seed: number): Livery {
  const rng = new Rng(seed);
  return {
    body: '#f7f7f5',
    stripes: [
      { color: '#f5a623', v0: 0.5, v1: 0.57 },
      { color: '#1b1b1b', v0: 1.0, v1: 1.06 },
    ],
    rearText: rng.pick(TROTRO_SLOGANS),
    frontBand: rng.pick(WINDSCREEN_BANNERS),
    plate: plate(rng),
  };
}

export function taxiLivery(seed: number, spec: VehicleSpec): Livery {
  const rng = new Rng(seed);
  const { length: L } = spec;
  const lowV = 0.38;
  const highV = 0.98;
  return {
    body: rng.pick(TAXI_BODIES),
    panels: [
      { u0: 0, u1: 0.85, v0: lowV, v1: highV, color: TAXI_CORNERS },
      { u0: L - 0.95, u1: L, v0: lowV, v1: highV, color: TAXI_CORNERS },
    ],
    plate: plate(rng),
  };
}

export function privateLivery(seed: number, bodies: readonly string[] = CAR_BODIES): Livery {
  const rng = new Rng(seed);
  return { body: rng.pick(bodies), plate: plate(rng) };
}

export function truckLivery(seed: number): Livery {
  const rng = new Rng(seed);
  return { body: rng.pick(TRUCK_BODIES), sideText: rng.pick(['KIA', 'ADOM TRANSPORT', 'NYAME NE HENE']), sideTextColor: '#ffffff', plate: plate(rng) };
}

export function pragyaLivery(seed: number): Livery {
  const rng = new Rng(seed);
  return { body: rng.pick(PRAGYA_BODIES), rearText: rng.pick(TROTRO_SLOGANS), plate: plate(rng) };
}
